import { API_URL } from '../config/constants';
import { withCsrfHeader } from '../utils/csrfUtils';
import {
  normalizeEndpointForTelemetry,
  recordApiBreadcrumb,
} from '../utils/instrumentation';
import logger, { safeErrorEventDetails } from '../utils/logger';

const isStateChanging = (method) => {
  return !['GET', 'HEAD', 'OPTIONS'].includes((method || 'GET').toUpperCase());
};

const now = () => (
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
);

const isAbortError = (error) => (
  error?.name === 'AbortError'
  || /aborted|abort/i.test(String(error?.message || ''))
);

const readAuthErrorCode = (response) => (
  response.status === 403
  && response.clone().json().then(({ code }) => code).catch(() => null)
);

class ApiService {
  constructor() {
    this.baseUrl = API_URL;
    this.refreshTokenCallback = null;
  }

  setRefreshTokenCallback(callback) {
    this.refreshTokenCallback = callback;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const method = options.method || 'GET';
    const startedAt = now();
    const safeEndpoint = normalizeEndpointForTelemetry(endpoint);
    const headers = isStateChanging(method)
      ? withCsrfHeader(options.headers || {})
      : (options.headers || {});

    try {
      recordApiBreadcrumb('request_start', { endpoint, method });
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        credentials: 'include'
      });

      recordApiBreadcrumb('request_end', {
        endpoint,
        method,
        status: response.status,
        durationMs: now() - startedAt,
      });
      return response;
    } catch (error) {
      const cancelled = isAbortError(error);
      recordApiBreadcrumb(cancelled ? 'request_cancelled' : 'request_error', {
        endpoint,
        method,
        durationMs: now() - startedAt,
      });
      if (!cancelled) {
        logger.errorEvent('network_failure', safeErrorEventDetails(error, { endpoint: safeEndpoint }));
        logger.error('API request failed:', error);
      }
      throw error;
    }
  }

  async authenticatedRequest(endpoint, options = {}, accessToken) {
    const refreshTokenCallback = this.refreshTokenCallback;
    const url = `${this.baseUrl}${endpoint}`;
    const method = options.method || 'GET';
    const startedAt = now();
    const safeEndpoint = normalizeEndpointForTelemetry(endpoint);
    const baseHeaders = isStateChanging(method)
      ? withCsrfHeader(options.headers || {})
      : (options.headers || {});

    const makeRequest = (token) => (
      fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...baseHeaders,
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include'
      })
    );

    recordApiBreadcrumb('authenticated_start', { endpoint, method });
    let response;

    try {
      response = await makeRequest(accessToken);
    } catch (error) {
      const cancelled = isAbortError(error);
      recordApiBreadcrumb(cancelled ? 'authenticated_cancelled' : 'authenticated_error', {
        endpoint,
        method,
        durationMs: now() - startedAt,
      });
      if (!cancelled) {
        logger.errorEvent('network_failure', safeErrorEventDetails(error, { endpoint: safeEndpoint }));
      }
      throw error;
    }

    // Refresh only when authentication itself is stale. Generic 403 responses
    // represent valid authorization or entitlement decisions and must not
    // rotate the session token.
    let authErrorCode = await readAuthErrorCode(response);
    if (
      refreshTokenCallback
      && (response.status === 401 || authErrorCode === 'ACCESS_TOKEN_INVALID_OR_EXPIRED')
    ) {
      logger.errorEvent('auth_token_invalid');
      const newToken = await refreshTokenCallback();
      if (newToken) {
        try {
          response = await makeRequest(newToken);
        } catch (error) {
          const cancelled = isAbortError(error);
          recordApiBreadcrumb(cancelled ? 'authenticated_cancelled' : 'authenticated_error', {
            endpoint,
            method,
            durationMs: now() - startedAt,
          });
          if (!cancelled) {
            logger.errorEvent('network_failure', safeErrorEventDetails(error, { endpoint: safeEndpoint }));
          }
          throw error;
        }
        authErrorCode = await readAuthErrorCode(response);
      }
    }

    if (authErrorCode === 'ACCESS_REVOKED' && refreshTokenCallback) {
      await refreshTokenCallback({ revoked: true });
    }

    // authenticated_end records the final status and endpoint; auth_token_invalid
    // already marks a refresh attempt, so extra 401/retry fields are duplicates.
    recordApiBreadcrumb('authenticated_end', {
      endpoint,
      status: response.status,
      durationMs: now() - startedAt,
    });
    return response;
  }
}

const apiService = new ApiService();

export default apiService;
